import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Head, Section, SectionHeader } from '@/components';
import { Hero } from '@/components/ui/Hero';
import { Marquee } from '@/components/ui/Marquee';
import { BentoGrid, BentoGridItem } from '@/components/ui/BentoGrid';
import { CASE_STUDIES, SERVICES } from '@/data/constants';
import { ArrowRight, Star, Shield, TrendingUp, Zap, Database, Workflow, Globe, Cpu } from 'lucide-react';

/**
 * Home page component.
 * Features hero section, case studies preview, value propositions, and CTA.
 *
 * @returns The home page.
 */
const Home: React.FC = () => {
  const techStack = [
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="react">
      <img
        src="/assets/tech/react.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">React</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="typescript">
      <img
        src="/assets/tech/typescript.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">TypeScript</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="nodejs">
      <img
        src="/assets/tech/nodejs.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Node.js</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="nextjs">
      <img
        src="/assets/tech/nextjs.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Next.js</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="tailwind">
      <img
        src="/assets/tech/tailwind.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Tailwind</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="postgresql">
      <img
        src="/assets/tech/postgresql.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">PostgreSQL</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="python">
      <img
        src="/assets/tech/python.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Python</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="fastapi">
      <img
        src="/assets/tech/fastapi.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">FastAPI</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="vercel">
      <img
        src="/assets/tech/vercel.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Vercel</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="neon">
      <img
        src="/assets/tech/neon.png"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Neon</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="framer">
      <img
        src="/assets/tech/framer.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Framer</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="docker">
      <img
        src="/assets/tech/docker.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Docker</span>
    </div>,
    <div className="flex items-center gap-3 px-6 py-3 rounded-xl bg-white/5 border border-white/5" key="zod">
      <img
        src="/assets/tech/zod.svg"
        alt=""
        className="w-6 h-6"
      />
      <span className="text-xl font-bold text-white whitespace-nowrap">Zod</span>
    </div>,
  ];

  return (
    <>
      <Head 
        title="Kivosystems | Custom Software & Digital Product Engineering"
        description="Premier software engineering consultancy. We build high-performance custom software, secure internal tools, and scalable digital products for ambitious businesses in Cyprus & London."
        canonicalUrl="https://www.kivosystems.com"
        url="https://www.kivosystems.com"
        image="/logo.svg"
        keywords="web design services, custom portfolio websites, ecommerce development, enterprise software, internal tools, fractional cto, business automation, nextjs experts cyprus, london digital agency"
      />
      
      <Hero />
      
      {/* Tech Stack Marquee */}
      <div className="py-10 bg-obsidian border-y border-white/5">
         <Marquee items={techStack} speed="normal" direction="left" />
      </div>

      {/* Services Spotlight */}
      <Section className="bg-obsidian border-b border-white/5 relative overflow-hidden">
        {/* Decorative background elements for high-tech feel */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-full pointer-events-none">
            <div className="absolute top-20 left-20 w-72 h-72 bg-sapphire-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
        </div>

        <SectionHeader label="Capabilities" title="Engineering & Product" className="text-center mb-16 relative z-10" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
          {SERVICES.map((service, idx) => (
            <Link 
              to="/services" 
              key={service.id}
              className="group relative flex flex-col p-6 rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden hover:bg-white/[0.04] transition-all duration-500"
            >
              {/* Hover Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-sapphire-500/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Tech Header */}
              <div className="flex items-center justify-between mb-6 relative">
                 <div className="p-3 bg-white/5 rounded-lg border border-white/5 group-hover:border-sapphire-500/30 transition-colors">
                    {service.id === 's1' && <Globe className="w-5 h-5 text-gray-300 group-hover:text-sapphire-400 transition-colors" />}
                    {service.id === 's2' && <Cpu className="w-5 h-5 text-gray-300 group-hover:text-sapphire-400 transition-colors" />}
                    {service.id === 's3' && <Workflow className="w-5 h-5 text-gray-300 group-hover:text-sapphire-400 transition-colors" />}
                    {service.id === 's4' && <Database className="w-5 h-5 text-gray-300 group-hover:text-sapphire-400 transition-colors" />}
                 </div>
                 <span className="font-mono text-[10px] text-gray-600 uppercase tracking-widest group-hover:text-sapphire-500/70 transition-colors">
                    0{idx + 1}
                 </span>
              </div>

              {/* Content */}
              <div className="relative flex-grow">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight group-hover:text-sapphire-200 transition-colors">
                    {service.category}
                  </h3>
                  <p className="text-sm text-gray-500 mb-6 font-medium leading-relaxed">
                    {service.items[0]?.description || 'Explore our professional services.'}
                  </p>
                  
                  {/* "Tech Specs" Features */}
                  <div className="space-y-2 mb-6">
                      {service.items[0]?.features.slice(0, 2).map(feature => (
                          <div key={feature} className="flex items-center text-[10px] uppercase tracking-wide text-gray-600 font-mono">
                              <div className="w-1 h-1 bg-gray-700 rounded-full mr-2 group-hover:bg-sapphire-500 transition-colors" />
                              {feature}
                          </div>
                      ))}
                  </div>
              </div>

              {/* Action */}
              <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between relative">
                <span className="text-xs font-bold text-gray-500 group-hover:text-white transition-colors">Details</span>
                <ArrowRight className="w-3 h-3 text-gray-600 group-hover:text-sapphire-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* Selected Work */}
      <Section className="bg-obsidian pt-0 beam-section">
        <SectionHeader label="Our Work" title="Recent Case Studies" className="text-center mb-16" />
        <BentoGrid>
          {CASE_STUDIES.slice(0, 4).map((study, i) => (
            <Link 
              to="/work" 
              key={study.id} 
              className={cn(
                "transition-all duration-300",
                i === 0 || i === 3 ? "md:col-span-4" : "md:col-span-2"
              )}
            >
                <BentoGridItem
                    title={study.title}
                    description={study.context}
                    header={
                        <img 
                            src={study.image} 
                            alt={study.title}
                            loading="lazy"
                            decoding="async"
                            className="object-cover object-top w-full h-full transition-transform duration-700 group-hover:scale-110" 
                        />
                    }
                    icon={<TrendingUp className="h-4 w-4 text-sapphire-500" />}
                />
            </Link>
          ))}
        </BentoGrid>
        
        <div className="mt-16 text-center">
            <Link to="/work" className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-colors">
                <span>View All Projects</span>
                <div className="w-8 h-px bg-gray-700 group-hover:w-12 group-hover:bg-white transition-all" />
                <ArrowRight className="w-4 h-4" />
            </Link>
        </div>
      </Section>

      {/* Philosophy / Value Prop */}
      <Section dark className="bg-obsidian-50/30">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="col-span-1">
             <h2 className="text-3xl font-display mb-6">Building the engine<br/>of your success.</h2>
             <p className="text-gray-400 leading-relaxed">
               We don't just build sites; we engineer digital foundations. From complex ecosystems to high-impact user experiences, we ensure your technical infrastructure is ready for scale.
             </p>
          </div>
          <div className="col-span-1 md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-12">
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
              <TrendingUp className="w-8 h-8 text-sapphire-500 mb-4" />
              <h3 className="text-lg font-bold mb-2">Designed for Results</h3>
              <p className="text-sm text-gray-400">
                A pretty website isn't enough. We build tools and sites designed to convert visitors into customers and streamline your work.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
              <Star className="w-8 h-8 text-amber-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Transparent Partnership</h3>
              <p className="text-sm text-gray-400">
                No jargon, no hidden fees. We communicate clearly at every step so you always know exactly what is happening.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
              <Shield className="w-8 h-8 text-emerald-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Reliable Foundations</h3>
              <p className="text-sm text-gray-400">
                We build on modern, secure standards. This means your digital presence is safe, fast, and easy to maintain in the long run.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-white/10 transition-colors">
              <Zap className="w-8 h-8 text-yellow-400 mb-4" />
              <h3 className="text-lg font-bold mb-2">Speed & Performance</h3>
              <p className="text-sm text-gray-400">
                Performance is a feature. We optimize every line of code to ensure your users have a fast, frictionless experience on any device.
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="text-center bg-obsidian relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-white/5 to-transparent opacity-30 pointer-events-none" />
        <div className="relative z-10">
            <h2 className="text-3xl md:text-6xl font-display mb-8">Let's build something <span className="text-sapphire-500 text-glow">amazing.</span></h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-12 text-lg">
            We accept a limited number of projects to ensure every client gets our full attention. Let's see if we're a good fit.
            </p>
            <Link to="/contact" className="inline-block px-10 py-4 bg-white text-obsidian text-lg font-bold rounded-full hover:bg-gray-100 transition-transform hover:scale-105 shadow-xl">
            Get a Free Quote
            </Link>
        </div>
      </Section>
    </>
  );
};

export default Home;
